import { alpha } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { AI, NEUTRAL } from 'theme/prismDesignTokens';

export const cardSx = (active: boolean, reviewed = false): SxProps<Theme> => ({
  // Contain the card to the panel width: never let inner content force the card
  // wider than its column (which produced a horizontal scrollbar). Clip on the X
  // axis ONLY - clipping Y would crop the card's detail when it is compressed.
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  overflowX: 'hidden',
  // The card list is a scrolling flex column; without this, many cards shrink to
  // fit the container (collapsing each to its title row) instead of the list scrolling.
  flexShrink: 0,
  borderRadius: 1,
  // Even 1px frame on three sides; the LEFT edge always reserves 2px so toggling
  // the active accent never shifts the card. Active state = a slim AI-purple
  // left accent + a soft AI-tinted drop shadow (replaces the old all-around
  // colored border, which rendered with uneven width and felt heavy).
  border: '1px solid',
  borderColor: 'divider',
  borderLeft: '2px solid',
  borderLeftColor: active ? AI : 'transparent',
  boxShadow: active ? `0 2px 10px ${alpha(AI, 0.28)}` : 'none',
  transition: 'box-shadow 150ms ease, border-left-color 150ms ease',
  p: 1.5,
  cursor: 'pointer',
  backgroundColor: 'background.paper',
  // Reviewed cards recede: dimmer background + reduced opacity so they read as
  // "already handled" without disappearing.
  ...(reviewed && { backgroundColor: NEUTRAL[50], opacity: 0.75 }),
});

// Pending accept/decline thumbs: a muted gray OUTLINE at rest so a card full of
// suggestions stays calm; on hover the hovered button alone swaps its outline icon
// for the filled one and takes its semantic color (green/red). The two icons are
// stacked and toggled via CSS so there is no re-render and no size shift.
export const thumbButtonSx = (activeColor: string): SxProps<Theme> => ({
  color: NEUTRAL[400],
  '& .thumb-fill': { display: 'none' },
  '& .thumb-outline': { display: 'inline-block' },
  '&:hover': {
    color: activeColor,
    backgroundColor: alpha(activeColor, 0.12),
  },
  '&:hover .thumb-outline': { display: 'none' },
  '&:hover .thumb-fill': { display: 'inline-block' },
});

// Compact "add note" / expander affordances kept visually subordinate.
export const noteToggleSx: SxProps<Theme> = {
  p: 0.25,
  color: 'text.secondary',
};

export const noteFieldSx: SxProps<Theme> = {
  mt: 0.75,
  '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 },
};

export const reviewedHeaderSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  minWidth: 0,
};

export const statusLabelSx = (accepted: boolean): SxProps<Theme> => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.25,
  flexShrink: 0,
  fontSize: '0.7rem',
  fontWeight: 600,
  color: accepted ? 'success.main' : 'error.main',
});

// Top-row label: the security name + metric. Stays on one row with the thumbs and
// shrinks (minWidth: 0) before they do, so the dismiss control is never pushed off.
export const titleSx: SxProps<Theme> = {
  fontWeight: 600,
  fontSize: '0.875rem',
  minWidth: 0,
  // Long titles wrap at word boundaries; a word only breaks mid-word as a LAST
  // resort, when it alone exceeds the line ('anywhere' / the legacy word-break
  // alias crushed min-content and split words at narrow panel widths: "Preferr-ed").
  overflowWrap: 'break-word',
};

// The suggested value on its own full-width line below the label/thumbs row. Slightly
// larger/bolder so it reads as the answer; wraps inside the card rather than overflowing.
export const valueSx: SxProps<Theme> = {
  mt: 0.5,
  width: '100%',
  fontWeight: 700,
  fontSize: '0.95rem',
  lineHeight: 1.3,
  color: 'text.primary',
  // Same last-resort wrapping as the title: never split a word that would fit
  // on its own line.
  overflowWrap: 'break-word',
};

// Citation-only cards: the CURRENT cell value the citation evidences. Quieter than
// the suggested-value line (no bold "answer" weight, secondary color) so it never
// reads as a proposed change - it is the user's own data the evidence backs.
export const citedValueSx: SxProps<Theme> = {
  mt: 0.5,
  width: '100%',
  fontWeight: 500,
  fontSize: '0.875rem',
  lineHeight: 1.3,
  color: 'text.secondary',
  overflowWrap: 'break-word',
};

// AI-purple solid chip with white text and only slightly rounded corners (not a
// full pill). Slim height; labels wrap to the next line via the row's flexWrap.
export const chipSx: SxProps<Theme> = {
  height: 'auto',
  borderRadius: 0.5,
  backgroundColor: AI,
  color: NEUTRAL.white,
  fontSize: '0.7rem',
  fontWeight: 600,
  maxWidth: '100%',
  '& .MuiChip-label': {
    px: 0.75,
    py: 0.25,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  },
};

export const detailWrapperSx: SxProps<Theme> = {
  mt: 1,
  pt: 1,
  borderTop: '1px solid',
  borderColor: 'divider',
  minWidth: 0,
  // Quoted evidence / commentary can be long; keep it inside the card width.
  overflowWrap: 'anywhere',
};

export const documentNameSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  mt: 1,
  color: 'text.secondary',
  fontSize: '0.78rem',
  minWidth: 0,
  overflowWrap: 'anywhere',
};

/** Exact raw current/suggested spellings for a numeric merge (full precision). */
export const exactValuesSx: SxProps<Theme> = {
  mt: 0.75,
  color: 'text.secondary',
  fontSize: '0.75rem',
  fontFamily: 'monospace',
  minWidth: 0,
  overflowWrap: 'anywhere',
};
